"use client"

import { useId } from "react"
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { formatNumber } from "@/lib/utils"

export interface AdvanceDeclinePoint {
  date: string
  advance: number
  decline: number
}

const POS = "#f87171" // 상승 우세
const NEG = "#60a5fa" // 하락 우세

// 0을 기준으로 위/아래 색이 갈리는 그라데이션 offset 계산 (recharts 공식 예제 패턴)
function gradientOffset(values: number[]) {
  const dataMax = Math.max(...values)
  const dataMin = Math.min(...values)
  if (dataMax <= 0) return 0
  if (dataMin >= 0) return 1
  return dataMax / (dataMax - dataMin)
}

export default function AdvanceDeclineChart({ data }: { data: AdvanceDeclinePoint[] }) {
  const gradId = useId()
  const chartData = data.map((d) => ({ ...d, net: d.advance - d.decline }))
  const offset = gradientOffset(chartData.map((d) => d.net))

  return (
    <ResponsiveContainer width="100%" height={240}>
      <ComposedChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id={`adFill-${gradId}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset={offset} stopColor={POS} stopOpacity={0.35} />
            <stop offset={offset} stopColor={NEG} stopOpacity={0.35} />
          </linearGradient>
          <linearGradient id={`adStroke-${gradId}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset={offset} stopColor={POS} stopOpacity={1} />
            <stop offset={offset} stopColor={NEG} stopOpacity={1} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
        <XAxis dataKey="date" tick={{ fill: "#a1a1aa", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#3f3f46" }} />
        <YAxis tick={{ fill: "#a1a1aa", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#3f3f46" }} width={48} tickFormatter={formatNumber} />
        <ReferenceLine y={0} stroke="#52525b" />
        <Tooltip
          content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null
            const row = payload[0]?.payload as (AdvanceDeclinePoint & { net: number }) | undefined
            if (!row) return null
            const color = row.net >= 0 ? POS : NEG
            return (
              <div style={{ background: "#18181b", border: "1px solid #3f3f46", borderRadius: 6, padding: "8px 10px", fontSize: 12 }}>
                <div style={{ color: "#e4e4e7", marginBottom: 4 }}>{label}</div>
                <div style={{ color, fontWeight: 500 }}>
                  차이 {row.net >= 0 ? "+" : ""}{formatNumber(row.net)}
                </div>
                <div style={{ color: "#a1a1aa", marginTop: 2 }}>
                  상승 {formatNumber(row.advance)} · 하락 {formatNumber(row.decline)}
                </div>
              </div>
            )
          }}
        />
        <Legend
          content={() => (
            <div style={{ display: "flex", justifyContent: "center", gap: 16, fontSize: 12, color: "#a1a1aa", paddingTop: 8 }}>
              {[
                { label: "상승 우세", color: POS },
                { label: "하락 우세", color: NEG },
              ].map(({ label, color }) => (
                <span key={label} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: color }} />
                  {label}
                </span>
              ))}
            </div>
          )}
        />
        <Area
          type="monotone"
          dataKey="net"
          stroke={`url(#adStroke-${gradId})`}
          fill={`url(#adFill-${gradId})`}
          strokeWidth={2}
          dot={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  )
}
